import { useRef, useState, type DragEvent } from 'react';
import './FileDrop.css';

interface FileDropProps {
  /** Fires with the dropped or chosen files. Never called with an empty list. */
  onFiles: (files: File[]) => void;
  /** `accept` for the hidden input; also gates what a drop will take. */
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Main line, e.g. "Drop a receipt here". */
  label: string;
  /** Second line — formats, size limits, whatever the caller enforces. */
  hint?: string;
  /** Row-sized: one line, no vertical padding to speak of. */
  compact?: boolean;
  /** Replaces the label once something is attached. */
  filled?: string | null;
}

/**
 * Drop target that is also a click-to-browse button.
 *
 * Generalised from the ingest dropzones (engagement letters, invoice PDFs,
 * vCards), which each hand-rolled the same four handlers. The `compact` variant
 * exists for form rows, where a 100px dashed box would dominate the field it
 * belongs to.
 *
 * `onFiles` receives everything dropped — filtering by type is the caller's job,
 * since only it knows what to say when the file is wrong.
 */
export function FileDrop({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  label,
  hint,
  compact = false,
  filled = null,
}: FileDropProps): JSX.Element {
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const take = (list: FileList | null): void => {
    if (!list || list.length === 0) return;
    onFiles(Array.from(list));
  };

  const open = (): void => {
    if (!disabled) inputRef.current?.click();
  };

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    // Stop here so a row-level drop doesn't ALSO hit a form-level handler and
    // get counted twice.
    e.stopPropagation();
    setOver(false);
    if (!disabled) take(e.dataTransfer.files);
  };

  return (
    <div
      className={[
        'filedrop',
        compact ? 'filedrop-compact' : '',
        over ? 'filedrop-over' : '',
        disabled ? 'filedrop-disabled' : '',
        filled ? 'filedrop-filled' : '',
      ].filter(Boolean).join(' ')}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-disabled={disabled}
    >
      <span className="filedrop-label">{filled ?? label}</span>
      {hint && !filled && <span className="filedrop-hint">{hint}</span>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        disabled={disabled}
        onChange={(e) => {
          take(e.target.files);
          // Reset so choosing the SAME file twice still fires onChange.
          e.target.value = '';
        }}
      />
    </div>
  );
}
